import React, { useState } from 'react';
import axios from 'axios';
import './UserManagement.css';

const UserManagement = ({ users, roles, permissionGroups, onAssignRole, onRemoveRole, onDeleteUser, refreshUsers }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedUser, setExpandedUser] = useState(null);
    const [selectedUserPermissions, setSelectedUserPermissions] = useState({});

    const toggleUserPermissions = (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
        } else {
            setExpandedUser(userId);
            const user = users.find(u => u.user_id === userId);
            
            // LOGIK: Wir laden ALLE Rechte, die der User gerade hat, in den State.
            // Dadurch sind sie im Menü "an" und können "aus" gemacht werden.
            const direct = user?.direct_permissions || [];
            const inherited = user?.inherited_permissions || [];
            
            // Kombinieren und Duplikate entfernen
            const allCurrent = [...new Set([...direct, ...inherited])];
            
            setSelectedUserPermissions(prev => ({
                ...prev,
                [userId]: allCurrent
            }));
        }
    };

    const toggleUserPermission = (userId, permissionName) => {
        setSelectedUserPermissions(prev => {
            const currentPerms = prev[userId] || [];
            if (currentPerms.includes(permissionName)) {
                return { ...prev, [userId]: currentPerms.filter(p => p !== permissionName) };
            } else {
                return { ...prev, [userId]: [...currentPerms, permissionName] };
            }
        });
    };

    const setPermissionsForUser = async (userId) => {
        const perms = selectedUserPermissions[userId] || [];
        try {
            const token = localStorage.getItem('discord_token');
            const API_BASE = process.env.REACT_APP_API_BASE;
            await axios.post(`${API_BASE}/api/set-user-permissions`, 
                { userId, permissions: perms }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Berechtigungen erfolgreich aktualisiert');
            if (refreshUsers) await refreshUsers();
        } catch (err) {
            alert('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
        }
    };

    const filteredUsers = users.filter(user =>
        ((user.server_nickname || user.global_name || user.username) || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_id.includes(searchTerm)
    );

    return (
        <div className="user-management">
            <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>👥 Benutzerverwaltung</h3>
                    <input 
                        type="text" 
                        placeholder="Suchen nach Name oder ID..." 
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ padding: '8px', borderRadius: '5px', border: '1px solid #444', background: '#222', color: '#fff' }}
                    />
                </div>
                
                <div className="card-body">
                    {(() => {
                        const allRoles = [...new Set(filteredUsers.flatMap(user => user.roles ? user.roles.split(',').map(r => r.trim()) : []))].sort();

                        const renderUserRow = (user) => (
                            <React.Fragment key={user.user_id}>
                                <tr style={{ borderBottom: '1px solid #222' }}>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img
                                                src={user.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
                                                alt="Avatar"
                                                style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '10px' }}
                                            />
                                            <span>{user.server_nickname || user.global_name || user.username}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px', color: '#888', fontSize: '0.85rem' }}>{user.user_id}</td>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                            {(() => {
                                                const roleList = user.roles ? user.roles.split(',').filter(r => r.trim()) : [];
                                                if (roleList.length === 0) {
                                                    return <span style={{ color: '#555', fontSize: '0.8rem' }}>Keine Rollen</span>;
                                                }
                                                return roleList.map((roleName, idx) => {
                                                    const cleanName = roleName.trim();
                                                    const roleObj = roles.find(r => r.name.trim().toLowerCase() === cleanName.toLowerCase()) || roles.find(r => String(r.id) === String(cleanName));
                                                    return (
                                                        <span key={idx} className="pill pill-primary" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                                                            {cleanName}
                                                            <button
                                                                onClick={() => {
                                                                    if (roleObj) onRemoveRole(user.user_id, roleObj.id);
                                                                }}
                                                                style={{ background: 'none', border: 'none', color: '#fff', marginLeft: '5px', cursor: 'pointer' }}
                                                            >×</button>
                                                        </span>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <select
                                                className="role-select"
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        onAssignRole(user.user_id, e.target.value);
                                                        e.target.value = "";
                                                    }
                                                }}
                                                style={{ background: '#333', color: '#fff', border: 'none', padding: '5px', borderRadius: '4px' }}
                                            >
                                                <option value="">+ Rolle</option>
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.id}>{role.name}</option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => toggleUserPermissions(user.user_id)}
                                                style={{ background: '#007bff', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                            >
                                                Berechtigungen
                                            </button>
                                            <button
                                                onClick={() => onDeleteUser(user.user_id)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
                                            >🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                                {expandedUser === user.user_id && (
                                    <tr>
                                        <td colSpan="4" style={{ padding: '20px', background: '#1a1a1a', borderLeft: '4px solid #007bff' }}>
                                            <div>
                                                <div style={{ marginBottom: '15px' }}>
                                                    <h4 style={{ margin: 0 }}>Berechtigungen bearbeiten</h4>
                                                    <small style={{ color: '#aaa' }}>Haken entfernen entzieht dem Nutzer das Recht (auch wenn er eine Rolle dafür hat).</small>
                                                </div>

                                                {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                                                    <div key={groupName} style={{ marginBottom: '20px' }}>
                                                        <h5 style={{ margin: '0 0 8px 0', color: '#007bff', borderBottom: '1px solid #333', paddingBottom: '3px' }}>{groupName}</h5>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                            {groupPerms.map((perm, idx) => {
                                                                const pName = typeof perm === 'string' ? perm : perm.name;
                                                                const isSelected = (selectedUserPermissions[user.user_id] || []).includes(pName);
                                                                const comesFromRole = (user.inherited_permissions || []).includes(pName);

                                                                return (
                                                                    <label
                                                                        key={perm.name}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            cursor: 'pointer',
                                                                            background: isSelected ? 'rgba(0, 123, 255, 0.1)' : '#222',
                                                                            padding: '5px 10px',
                                                                            borderRadius: '4px',
                                                                            border: isSelected ? (comesFromRole ? '1px solid #007bff' : '1px solid #28a745') : '1px solid #444',
                                                                            fontSize: '0.85rem'
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={() => toggleUserPermission(user.user_id, perm.name)}
                                                                            style={{ marginRight: '8px' }}
                                                                        />
                                                                        <span style={{ color: isSelected ? '#fff' : '#888' }}>
                                                                            {perm.name}
                                                                        </span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                                <button
                                                    onClick={() => setPermissionsForUser(user.user_id)}
                                                    className="btn-primary"
                                                    style={{ marginTop: '10px', padding: '10px 20px', fontWeight: 'bold' }}
                                                >
                                                    Änderungen speichern
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );

                        return (
                            <>
                                {allRoles.map(role => {
                                    const usersWithRole = filteredUsers.filter(user => user.roles && user.roles.split(',').map(r => r.trim()).includes(role));
                                    if (usersWithRole.length === 0) return null;
                                    return (
                                        <div key={role} style={{ marginBottom: '30px', background: '#1a1a1a', borderRadius: '8px', padding: '15px', border: '1px solid #333' }}>
                                            <h4 style={{ color: '#fff', marginBottom: '10px', borderBottom: '1px solid #444', paddingBottom: '5px' }}>{role}</h4>
                                            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                                                <thead>
                                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #444' }}>
                                                        <th style={{ padding: '12px', width: '30%' }}>Benutzer</th>
                                                        <th style={{ padding: '12px', width: '20%' }}>Discord ID</th>
                                                        <th style={{ padding: '12px', width: '25%' }}>Aktuelle Rollen</th>
                                                        <th style={{ padding: '12px', width: '25%' }}>Aktionen</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {usersWithRole.map(user => renderUserRow(user))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })}
                                {/* Users without roles */}
                                {(() => {
                                    const usersWithoutRoles = filteredUsers.filter(user => !user.roles || user.roles.split(',').every(r => r.trim() === ''));
                                    if (usersWithoutRoles.length === 0) return null;
                                    return (
                                        <div style={{ marginBottom: '30px', background: '#1a1a1a', borderRadius: '8px', padding: '15px', border: '1px solid #333' }}>
                                            <h4 style={{ color: '#fff', marginBottom: '10px', borderBottom: '1px solid #444', paddingBottom: '5px' }}>Keine Rollen</h4>
                                            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                                                <thead>
                                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #444' }}>
                                                        <th style={{ padding: '12px', width: '30%' }}>Benutzer</th>
                                                        <th style={{ padding: '12px', width: '20%' }}>Discord ID</th>
                                                        <th style={{ padding: '12px', width: '25%' }}>Aktuelle Rollen</th>
                                                        <th style={{ padding: '12px', width: '25%' }}>Aktionen</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {usersWithoutRoles.map(user => renderUserRow(user))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}
                            </>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default UserManagement;